import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import confetti from "canvas-confetti";
import { XCircle, Loader2, Heart, ArrowRight, Repeat } from "lucide-react";
import { getPaymentStatus } from "@/lib/api";
import { Reveal } from "@/components/Reveal";

export default function DonationResult({ status }) {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState(status === "cancel" ? "cancel" : "polling");
  const [info, setInfo] = useState(null);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, []);

  useEffect(() => {
    if (status === "cancel" || !sessionId) return;
    let tries = 0;
    let timer;
    const poll = async () => {
      try {
        const data = await getPaymentStatus(sessionId);
        setInfo(data);
        if (data.payment_status === "paid") return setState("success");
        if (["failed", "expired"].includes(data.payment_status)) return setState("failed");
      } catch {
        /* keep trying */
      }
      if (++tries >= 8) return setState("timeout");
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId, status]);

  useEffect(() => {
    if (state !== "success") return;
    const colors = ["#C9962F", "#e0b25a", "#ffffff", "#0F1F3D"];
    const fire = (particleRatio, opts) =>
      confetti({ origin: { y: 0.4 }, colors, particleCount: Math.floor(200 * particleRatio), ...opts });
    fire(0.25, { spread: 26, startVelocity: 55 });
    fire(0.2, { spread: 60 });
    fire(0.35, { spread: 100, decay: 0.91, scalar: 0.9 });
    fire(0.1, { spread: 120, startVelocity: 25, decay: 0.92, scalar: 1.2 });
    fire(0.1, { spread: 120, startVelocity: 45 });
  }, [state]);

  return (
    <section className="relative flex min-h-[80svh] items-center justify-center overflow-hidden px-5 pt-24">
      <div className="pointer-events-none absolute left-1/2 top-1/3 h-72 w-72 -translate-x-1/2 rounded-full bg-gold/10 blur-3xl" />
      <div className="relative w-full max-w-lg text-center" data-testid={`donation-result-${state}`}>
        {state === "polling" && (
          <>
            <Loader2 className="mx-auto h-14 w-14 animate-spin text-gold" />
            <h1 className="mt-8 font-heading text-3xl font-bold">Confirming your donation…</h1>
            <p className="mt-4 text-muted-foreground">Please hold on while we verify your payment securely.</p>
          </>
        )}

        {state === "success" && (
          <Reveal>
            <motion.div
              initial={{ scale: 0, rotate: -20 }}
              animate={{ scale: [0, 1.3, 1] }}
              transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
              className="mx-auto grid h-24 w-24 place-items-center rounded-full bg-gold/15"
            >
              <motion.div animate={{ scale: [1, 1.18, 1] }} transition={{ duration: 1.4, repeat: Infinity, ease: "easeInOut" }}>
                <Heart className="h-12 w-12 text-gold" fill="currentColor" />
              </motion.div>
            </motion.div>
            <h1 className="mt-8 font-heading text-4xl font-bold tracking-tight">Thank you.</h1>
            <p className="mt-4 text-lg text-muted-foreground">
              Your {info?.frequency === "monthly" ? "monthly " : ""}gift
              {info?.amount ? ` of $${info.amount}` : ""} is making brighter futures possible.
              {info?.frequency !== "monthly" && " A receipt is on its way to your inbox."}
            </p>
            <div className="mt-10 flex flex-wrap justify-center gap-4">
              <Link to="/" className="inline-flex items-center gap-2 rounded-full bg-gold px-7 py-3.5 text-sm font-semibold text-navy-deep transition-transform hover:-translate-y-0.5">
                Back Home <ArrowRight className="h-4 w-4" />
              </Link>
              <Link to="/impact" className="inline-flex items-center gap-2 rounded-full border border-border px-7 py-3.5 text-sm font-semibold transition-colors hover:border-gold hover:text-gold">
                See Our Impact
              </Link>
            </div>

            {info?.frequency !== "monthly" && (
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6, duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
                className="mx-auto mt-12 max-w-md rounded-2xl border border-gold/30 bg-gold/[0.06] p-6 text-center"
                data-testid="recurring-nudge"
              >
                <span className="mx-auto grid h-11 w-11 place-items-center rounded-full bg-gold/15">
                  <Repeat className="h-5 w-5 text-gold" />
                </span>
                <h2 className="mt-4 font-heading text-lg font-bold tracking-tight">Make it monthly?</h2>
                <p className="mt-2 text-sm text-muted-foreground">
                  A small recurring gift creates steady, lasting impact all year long — and you can change or cancel anytime.
                </p>
                <Link
                  to="/donate?frequency=monthly"
                  data-testid="recurring-nudge-cta"
                  className="mt-5 inline-flex items-center gap-2 rounded-full bg-gold px-6 py-3 text-sm font-semibold text-navy-deep transition-transform hover:-translate-y-0.5"
                >
                  <Heart className="h-4 w-4" fill="currentColor" /> Give Monthly
                </Link>
              </motion.div>
            )}
          </Reveal>
        )}

        {(state === "cancel" || state === "failed" || state === "timeout") && (
          <Reveal>
            <XCircle className="mx-auto h-16 w-16 text-muted-foreground" />
            <h1 className="mt-8 font-heading text-3xl font-bold tracking-tight">
              {state === "cancel" ? "Donation cancelled" : state === "timeout" ? "Still processing" : "Payment not completed"}
            </h1>
            <p className="mt-4 text-muted-foreground">
              {state === "timeout"
                ? "Your payment is taking a moment. If you completed checkout, your receipt will still arrive by email."
                : "No charge was made. Whenever you're ready, we'd be grateful for your support."}
            </p>
            <div className="mt-10 flex flex-wrap justify-center gap-4">
              <Link to="/donate" className="inline-flex items-center gap-2 rounded-full bg-gold px-7 py-3.5 text-sm font-semibold text-navy-deep transition-transform hover:-translate-y-0.5">
                <Heart className="h-4 w-4" /> Try Again
              </Link>
              <Link to="/" className="inline-flex items-center gap-2 rounded-full border border-border px-7 py-3.5 text-sm font-semibold transition-colors hover:border-gold hover:text-gold">
                Back Home
              </Link>
            </div>
          </Reveal>
        )}
      </div>
    </section>
  );
}
